import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  accountant: "Accountant",
  sales: "Sales Staff",
  booking: "Booking Staff",
  viewer: "Viewer",
};

const ALL = ["dashboard", "bookings", "quotations", "customers", "agents", "services",
  "invoices", "notes", "payments", "expenses", "gst", "reports", "ledgers", "settings",
  "users", "audit", "backup", "search", "files", "notifications"];
const READ_OPS = ["dashboard", "bookings", "quotations", "customers", "agents", "services",
  "invoices", "notes", "payments", "search", "files", "notifications"];

const PERMS = {
  super_admin: { read: ALL, write: ALL },
  admin: { read: ALL, write: ALL },
  accountant: {
    read: ALL.filter((m) => m !== "users"),
    write: ["invoices", "notes", "payments", "expenses", "gst", "customers", "agents",
      "services", "bookings", "quotations", "files", "backup"],
  },
  sales: {
    read: [...READ_OPS, "expenses", "reports", "ledgers"],
    write: ["bookings", "quotations", "customers", "invoices", "payments", "files"],
  },
  booking: {
    read: READ_OPS,
    write: ["bookings", "quotations", "customers", "files"],
  },
  viewer: {
    read: ["dashboard", "bookings", "quotations", "customers", "agents", "services",
      "invoices", "notes", "payments", "expenses", "gst", "reports", "ledgers", "search",
      "notifications"],
    write: [],
  },
};

export function can(role, module, write = false) {
  const p = PERMS[role] || { read: [], write: [] };
  return (write ? p.write : p.read).includes(module);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
      window.location.href = "/login";
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
